import {
  TextInput,
  NumberInput,
  ColorInput,
  Button,
  Stack,
  Group,
  SegmentedControl,
  Textarea,
} from '@mantine/core';
import { Caption, CaptionStyle, SpecialEffect } from '../../../types';
import { useCallback, memo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../../assets/Tab.module.css';
import FontAccordion from './FontAccordion';
import DarkColorPicker from './DarkColorPicker';
import CaptionStylePresets from './CaptionStylePresets';
import SimpleAccordion from './SimpleAccordion';
import TextCapitalizationButtons from '../../shared/TextCapitalizationButtons';
import TextAlignDropdown from '../../shared/TextAlignDropdown';

const colorNameToHex: { [key: string]: string } = {
  red: '#ff0000',
  green: '#00ff00',
  blue: '#0000ff',
  yellow: '#ffff00',
  purple: '#800080',
  cyan: '#00ffff',
  black: '#000000',
  white: '#ffffff',
  gray: '#808080',
  orange: '#ffa500',
  brown: '#a52a2a',
  pink: '#ffc0cb',
};

interface EditSegmentTooltipProps {
  segment: Caption | null;
  isOpen: boolean;
  onClose: () => void;
  targetId: string | null;
  setCaptions: React.Dispatch<React.SetStateAction<Caption[]>>;
  editMode?: 'captions' | 'titles'; // NEW: edit mode prop
  currentTime?: number; // NEW: current timeline position for adding new items
  applyToAll?: boolean; // NEW: receive applyToAll state from parent
}

const EditSegmentTooltip: React.FC<EditSegmentTooltipProps> = ({
  segment,
  isOpen,
  onClose,
  targetId,
  setCaptions,
  editMode = 'captions', // NEW: default to captions
  currentTime, // NEW
  applyToAll = false, // NEW: receive from parent
}) => {
  const { t } = useTranslation('captionEditor');
  
  if (!segment || !targetId) return null;


  // State for the segmented control - initialize from segment's actual effect
  const [selectedOption, setSelectedOption] = useState<SpecialEffect | null>(
    segment.style.specialEffect || null
  );

  // Update selectedOption when segment changes
  useEffect(() => {
    setSelectedOption(segment.style.specialEffect || null);
  }, [segment.id, segment.style.specialEffect]);

  const handleOptionChange = (value: SpecialEffect) => {
    // Toggle: if clicking the same effect, uncheck it (set to null)
    const newValue = selectedOption === value ? null : value;
    setSelectedOption(newValue);
    // Update the caption style - null will remove the effect
    handleStyleUpdate('specialEffect', newValue);
  };

  const handleCaptionUpdate = useCallback(
    (key: keyof Caption, value: number | string) => {
      setCaptions((prevCaptions) =>
        prevCaptions.map((caption) =>
          caption.id === segment.id ? { ...caption, [key]: value } : caption
        )
      );
    },
    [segment.id, setCaptions]
  );

  // Specialized handler for text capitalization that respects applyToAll
  const handleCapitalizationUpdate = useCallback(
    (newText: string, transformFn?: (text: string) => string) => {
      console.log('=== handleCapitalizationUpdate ===');
      console.log('applyToAll:', applyToAll);
      console.log('transformFn exists:', !!transformFn);
      console.log('newText:', newText);
      
      setCaptions((prevCaptions) => {
        if (applyToAll && transformFn) {
          console.log('Applying transformation to all captions');
          const result = prevCaptions.map((caption) => {
            const transformed = transformFn(caption.text);
            console.log(`  "${caption.text}" -> "${transformed}"`);
            return { ...caption, text: transformed };
          });
          return result;
        } else {
          console.log('Applying to single caption:', segment.id);
          return prevCaptions.map((caption) =>
            caption.id === segment.id ? { ...caption, text: newText } : caption
          );
        }
      });
    },
    [segment.id, setCaptions, applyToAll]
  );

  const handleColorChange = useCallback(
    (styleKey: 'color' | 'stroke' | 'shadowColor', value: string) => {
      console.log("================handleColorChange");
      const lowerValue = value.toLowerCase();
      const hexColor = colorNameToHex[lowerValue] || value;
      setCaptions((prevCaptions) =>
        prevCaptions.map((caption) =>
          applyToAll? { ...caption, style: { ...caption.style, [styleKey]: hexColor } } :
          caption.id === segment.id
            ? { ...caption, style: { ...caption.style, [styleKey]: hexColor } }
            : caption
        )
      );
    },
    [segment.id, setCaptions, applyToAll]
  );

  const handleStyleUpdate = useCallback(
    (styleKey: keyof Caption['style'], value: number | string | null) => {
      setCaptions((prevCaptions) =>
        applyToAll? prevCaptions.map((caption) => {
          const newStyle = { ...caption.style } as any;
          if (value === null) {
            delete newStyle[styleKey];
          } else {
            newStyle[styleKey] = value;
          }
          return { ...caption, style: newStyle };
        }) :
        prevCaptions.map((caption) => {
          if (caption.id === segment.id) {
            const newStyle = { ...caption.style } as any;
            if (value === null) {
              delete newStyle[styleKey];
            } else {
              newStyle[styleKey] = value;
            }
            return { ...caption, style: newStyle };
          }
          return caption;
        })
      );
    },
    [segment.id, setCaptions, applyToAll]
  );

  // When the current segment's start time is changed,
  // update its start and, if present, set the previous segment's end equal to the new start.
  const handleStartTimeChange = (newStart: number) => {
    setCaptions((prevCaptions) => {
      const updated = [...prevCaptions];
      const index = updated.findIndex((caption) => caption.id === segment.id);
      if (index === -1) return prevCaptions;
      // Update current segment's start time.
      updated[index] = { ...updated[index], start: newStart };
      // Update previous segment's end time, if available.
      if (index > 0) {
        updated[index - 1] = { ...updated[index - 1], end: newStart };
      }
      return updated;
    });
  };

  // When the current segment's end time is changed,
  // update its end and, if present, set the next segment's start equal to the new end.
  const handleEndTimeChange = (newEnd: number) => {
    setCaptions((prevCaptions) => {
      const updated = [...prevCaptions];
      const index = updated.findIndex((caption) => caption.id === segment.id);
      if (index === -1) return prevCaptions;
      // Update current segment's end time.
      updated[index] = { ...updated[index], end: newEnd };
      // Update next segment's start time, if available.
      if (index < updated.length - 1) {
        updated[index + 1] = { ...updated[index + 1], start: newEnd };
      }
      return updated;
    });
  };

  // Font family list
  const fontFamilies = [
    { name: 'Bebas Neue', className: 'bebas-neue-regular' },
    { name: 'Anton', className: 'anton-regular' },
    { name: 'Lilita One', className: 'lilita-one-regular' },
    { name: 'Alfa Slab One', className: 'alfa-slab-one-regular' },
    { name: 'Rubik Mono One', className: 'rubik-mono-one-regular' },
    { name: 'Passion One', className: 'passion-one-regular' },
    { name: 'Concert One', className: 'concert-one-regular' },
    { name: 'Luckiest Guy', className: 'luckiest-guy-regular' },
    { name: 'Titan One', className: 'titan-one-regular' },
    { name: 'Paytone One', className: 'paytone-one-regular' },
    { name: 'Crete Round', className: 'crete-round-regular' },
    { name: 'Bangers', className: 'bangers-regular' },
    { name: 'Atma', className: 'atma-regular' },
    { name: 'Chewy', className: 'chewy-regular' },
    { name: 'Chicle', className: 'chicle-regular' },
    { name: 'Ranchers', className: 'ranchers-regular' },
    { name: 'Peralta', className: 'peralta-regular' },
    { name: 'Original Surfer', className: 'original-surfer-regular' },
    { name: 'Poetsen One', className: 'poetsen-one-regular' },
  ];

  const refDoneButton = useRef<HTMLButtonElement>(null);

  console.log("--->", segment);
  console.log(`EditSegmentTooltip: segment: ${JSON.stringify(segment)}`);

  function onSegmentSplit() {
    const newCaptionData: Caption = { ...segment! };
    setCaptions((prevCaptions: Caption[]) => {
      const index = prevCaptions.findIndex(
        (caption) => caption.id === segment!.id
      );
      if (index === -1) return prevCaptions;
      const mid = segment!.start + (segment!.end - segment!.start) / 2;
      const updatedCurrentSegment: Caption = { ...segment!, end: mid };
      const newCaption: Caption = {
        ...newCaptionData,
        text: '',
        style: segment!.style,
        start: mid,
        id: (parseInt(segment!.id, 10) + 1).toString(),
      };
      const updatedCaptions = prevCaptions.map((caption) => {
        if (parseInt(caption.id, 10) > parseInt(segment!.id, 10)) {
          return { ...caption, id: (parseInt(caption.id, 10) + 1).toString() };
        }
        return caption;
      });
      return [
        ...updatedCaptions.slice(0, index),
        updatedCurrentSegment,
        newCaption,
        ...updatedCaptions.slice(index + 1),
      ];
    });
  }

  // Handle adding title at current time
  function handleAddTitleAtCurrentTime(): void {
    if (!currentTime && currentTime !== 0) return;
    
    const newTitle: Caption = {
      id: Date.now().toString(),
      start: currentTime,
      end: currentTime + 3, // Default 3 second duration
      text: editMode === 'titles' 
        ? t('captionEditor.editSegment.defaultText.newTitle')
        : t('captionEditor.editSegment.defaultText.newCaption'),
      style: {
        fontSize: editMode === 'titles' ? 48 : 32, // Larger font for titles
        color: '#ffffff',
        stroke: '#000000',
        strokeWidth: 2,
        fontFamily: 'Arial',
        yPosition: editMode === 'titles' ? 40 : 160, // Position titles at top (20%), captions at bottom (80%)
      },
      itemType: editMode // Mark with appropriate type
    };
    
    setCaptions(prevCaptions => [...prevCaptions, newTitle]);
  }

  function onMedaiItemDelete() {
    setCaptions((prevCaptions: Caption[]) => {
      return prevCaptions.filter((caption) => caption.id !== segment!.id);
    });
  }

  return (
    <div className={styles.container}>
      <Stack>

      <Group className={styles.buttonGroup} style={{marginBottom: "12px"}}>
          {editMode === 'titles' ? (
            <button 
              onClick={handleAddTitleAtCurrentTime} 
              className={styles.button}
              style={{
                background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                color: '#ffffff',
                border: 'none'
              }}
            >
              {t('captionEditor.editSegment.buttons.addTitle')}
            </button>
          ) : null}
          <button onClick={onSegmentSplit} className={styles.button}>
            {t('captionEditor.editSegment.buttons.split')}
          </button>
          <button onClick={onMedaiItemDelete} className={styles.button} style={{color:"#c86666"}}>
            {t('captionEditor.editSegment.buttons.delete')}
          </button>
      </Group>

      <label className={styles.label}>{t('captionEditor.editSegment.labels.captionText')}</label>
        <textarea
          value={segment.text}
          onChange={(e) => handleCaptionUpdate('text', e.target.value)}
          className={`${styles.textarea}`}
        />

        {/* Text Capitalization */}
        <div style={{ marginTop: '-14px' }}>
          <label className={styles.label}>{t('captionEditor.editSegment.labels.textCapitalization')}</label>
          <TextCapitalizationButtons
            currentText={segment.text}
            onTextChange={handleCapitalizationUpdate}
          />
        </div>

      <div style={{marginTop:'8px'}}><label className={styles.label}>{t('captionEditor.editSegment.labels.captionStyles')}</label></div>
      <div
          style={{
            maxHeight: '100px',
            overflowY: 'auto',
            border: '1px solid #555',
            padding: '8px',
            backgroundColor: '#2d2d2d'
          }}
        >
          <CaptionStylePresets
            segment={segment}
            updateStyle={(newStyle: CaptionStyle) => {
              // Update the segment's style
              setCaptions((prevCaptions) =>
                applyToAll? prevCaptions.map((caption) => ({ ...caption, style: newStyle })) :
                prevCaptions.map((caption) =>
                  caption.id === segment.id ? { ...caption, style: newStyle } : caption
                )
              );
            }}
          />
        </div>

        <Stack  style={{marginTop:'18px'}}>
        {/* Here is our accordion with font previews */}
         <label className={styles.label}>{t('captionEditor.editSegment.labels.fontFamily')}</label>
          <FontAccordion
            fontFamilies={fontFamilies}
            segment={segment}
            handleStyleUpdate={handleStyleUpdate}
          />
        </Stack>

        <Group style={{marginTop:"12px"}} className={styles.startEndContainer} >
          <div>
            <label className={styles.label}>{t('captionEditor.editSegment.labels.start')}</label>
              <input
                type="number"
                value={segment.start}
                min={0}
                step={0.1}
                onChange={(e) => {
                  handleStartTimeChange(+e.target.value);
                }}
                className={`${styles.input} ${styles.inputSm}`}
            />
          </div>
          <div>
            <label className={styles.label}>{t('captionEditor.editSegment.labels.end')}</label>
              <input
                type="number"
                value={segment.end}
                min={0}
                step={0.1}
                onChange={(e) => {
                  handleEndTimeChange(+e.target.value);
                }}
                className={`${styles.input} ${styles.inputSm}`}
            />
          </div>
          
        </Group>
        <SimpleAccordion title={t('captionEditor.editSegment.labels.more')} defaultOpen={false} styles={{headerStyle: {marginTop:'12px'}}}>

          <Group style={{marginTop:"12px"}} className={styles.startEndContainer}>
            <div>
              <label className={styles.label}>{t('captionEditor.editSegment.labels.textSize')}</label>
              <input
                type="number"
                value={segment.style.fontSize}
                step={1}
                onChange={(e) => handleStyleUpdate('fontSize', e.target.value)}
                className={`${styles.input} ${styles.inputSm}`}
              />
            </div>
            
            <DarkColorPicker
              label={t('captionEditor.editSegment.labels.textColor')}
              value={segment.style.color}
              onChange={(value: string) => handleColorChange('color', value)}
            />
          </Group>

          {/* Stroke */}
          <Group style={{marginTop:"12px"}} className={styles.startEndContainer}>
            <div>
              <label className={styles.label}>{t('captionEditor.editSegment.labels.strokeWidth')}</label>
              <input
                  type="number"
                  value={segment.style.strokeWidth}
                  step={1}
                  onChange={(e) => handleStyleUpdate('strokeWidth', e.target.value)}
                  className={`${styles.input} ${styles.inputSm}`}
              />
            </div>
            <DarkColorPicker
              label={t('captionEditor.editSegment.labels.strokeColor')}
              value={segment.style.shadowColor ||''}
              onChange={(value) => handleColorChange('stroke', value)}
            />
          </Group>

          {/* Shadow */}
          <Group style={{marginTop:"12px"}} className={styles.startEndContainer}>
            <div>
              <label className={styles.label}>{t('captionEditor.editSegment.labels.shadowXY')}</label>
              <input
                value={segment.style.shadowxz}
                step={1}
                type="number"
                onChange={(e) => handleStyleUpdate('shadowxz', e.target.value )}
                className={`${styles.input} ${styles.inputSm}`}
              />
            </div>
            <DarkColorPicker
              label={t('captionEditor.editSegment.labels.shadowColor')}
              value={segment.style.shadowColor ||'white'}
              onChange={(value) => handleColorChange('shadowColor', value)}
            />
          </Group>
          <Group>
            <Group 
              grow
              style={{
                marginTop: '12px',
                '--ni-chevron-size': '16px',
                '--ci-eye-dropper-icon-size': '16px',
              } as React.CSSProperties}
            >
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <label className={styles.label}>{t('captionEditor.editSegment.labels.yPosition')}</label>
                  <input
                    type="number"
                    value={segment.style.yPosition || 20}
                    step={1}
                    onChange={(e) => handleStyleUpdate('yPosition', e.target.value)}
                    className={`${styles.input} ${styles.inputSm}`}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className={styles.label}>{t('captionEditor.editSegment.labels.xPosition')}</label>
                  <input
                    type="number"
                    value={segment.style.xPosition || 50}
                    step={1}
                    onChange={(e) => handleStyleUpdate('xPosition', e.target.value)}
                    className={`${styles.input} ${styles.inputSm}`}
                  />
                </div>
              </div>
            </Group>

            {/* Text Align Dropdown */}
            <Group style={{ marginTop: '12px' }}>
              <TextAlignDropdown
                value={segment.style.textAlign || 'center'}
                onChange={(newAlign) => {
                  // Update xPosition based on alignment
                  let newXPosition: number;
                  if (newAlign === 'left') {
                    newXPosition = 10; // 10% from left
                  } else if (newAlign === 'center') {
                    newXPosition = 50; // Center
                  } else if (newAlign === 'right') {
                    newXPosition = 90; // 90% from left (near right edge)
                  } else {
                    newXPosition = segment.style.xPosition || 50;
                  }
                  
                  // Update both textAlign and xPosition
                  setCaptions((prevCaptions) =>
                    applyToAll 
                      ? prevCaptions.map((caption) => ({
                          ...caption,
                          style: {
                            ...caption.style,
                            textAlign: newAlign,
                            xPosition: newXPosition
                          }
                        }))
                      : prevCaptions.map((caption) =>
                          caption.id === segment.id
                            ? {
                                ...caption,
                                style: {
                                  ...caption.style,
                                  textAlign: newAlign,
                                  xPosition: newXPosition
                                }
                              }
                            : caption
                        )
                  );
                }}
                label={t('captionEditor.editSegment.labels.textAlign')}
                className={styles.input}
              />
            </Group>
            
            {/* Special Effects Section */}
            <div style={{marginTop:'18px'}}>
              <label className={styles.label}>{t('captionEditor.editSegment.labels.specialEffects')}</label>
            </div>
            
            <div className={styles.startEndContainer} style={{marginTop:"12px"}}>
              <button
                  style={{
                    border: selectedOption === SpecialEffect.FADEOUT ? '2px solid #8b5cf6' : '1px solid #555',
                    background: selectedOption === SpecialEffect.FADEOUT ? 'linear-gradient(135deg, #8b5cf6, #ec4899)' : 'transparent',
                    color: selectedOption === SpecialEffect.FADEOUT ? '#ffffff' : '#e0e0e0'
                  }}
                  onClick={() => handleOptionChange(SpecialEffect.FADEOUT)}
                  className={`${styles.button} `}
              >
                {t('captionEditor.editSegment.specialEffects.fadeOut')}
              </button>
              <button
                style={{
                  border: selectedOption === SpecialEffect.ZOOMOUT ? '2px solid #8b5cf6' : '1px solid #555',
                  background: selectedOption === SpecialEffect.ZOOMOUT ? 'linear-gradient(135deg, #8b5cf6, #ec4899)' : 'transparent',
                  color: selectedOption === SpecialEffect.ZOOMOUT ? '#ffffff' : '#e0e0e0'
                }}
                onClick={() => handleOptionChange(SpecialEffect.ZOOMOUT)}
                className={`${styles.button} `}
              >
                {t('captionEditor.editSegment.specialEffects.zoomOut')}
              </button>
              <button
                style={{
                  border: selectedOption === SpecialEffect.SLIDE_IN ? '2px solid #8b5cf6' : '1px solid #555',
                  background: selectedOption === SpecialEffect.SLIDE_IN ? 'linear-gradient(135deg, #8b5cf6, #ec4899)' : 'transparent',
                  color: selectedOption === SpecialEffect.SLIDE_IN ? '#ffffff' : '#e0e0e0'
                }}
                onClick={() => handleOptionChange(SpecialEffect.SLIDE_IN)}
                className={`${styles.button} `}
              >
                {t('captionEditor.editSegment.specialEffects.slideIn')}
              </button>
              <button
                style={{
                  border: selectedOption === SpecialEffect.BOUNCE ? '2px solid #8b5cf6' : '1px solid #555',
                  background: selectedOption === SpecialEffect.BOUNCE ? 'linear-gradient(135deg, #8b5cf6, #ec4899)' : 'transparent',
                  color: selectedOption === SpecialEffect.BOUNCE ? '#ffffff' : '#e0e0e0'
                }}
                onClick={() => handleOptionChange(SpecialEffect.BOUNCE)}
                className={`${styles.button} `}
              >
                {t('captionEditor.editSegment.specialEffects.bounce')}
              </button>
              <button
                style={{
                  border: selectedOption === SpecialEffect.WAVE ? '2px solid #8b5cf6' : '1px solid #555',
                  background: selectedOption === SpecialEffect.WAVE ? 'linear-gradient(135deg, #8b5cf6, #ec4899)' : 'transparent',
                  color: selectedOption === SpecialEffect.WAVE ? '#ffffff' : '#e0e0e0'
                }}
                onClick={() => handleOptionChange(SpecialEffect.WAVE)}
                className={`${styles.button} `}
              >
                {t('captionEditor.editSegment.specialEffects.wave')}
              </button>
              <button
                style={{
                  border: selectedOption === SpecialEffect.SPIN_ZOOM ? '2px solid #8b5cf6' : '1px solid #555',
                  background: selectedOption === SpecialEffect.SPIN_ZOOM ? 'linear-gradient(135deg, #8b5cf6, #ec4899)' : 'transparent',
                  color: selectedOption === SpecialEffect.SPIN_ZOOM ? '#ffffff' : '#e0e0e0'
                }}
                onClick={() => handleOptionChange(SpecialEffect.SPIN_ZOOM)}
                className={`${styles.button} `}
              >
                {t('captionEditor.editSegment.specialEffects.spinZoom')}
              </button>
              <button
                style={{
                  border: selectedOption === SpecialEffect.PULSE ? '2px solid #8b5cf6' : '1px solid #555',
                  background: selectedOption === SpecialEffect.PULSE ? 'linear-gradient(135deg, #8b5cf6, #ec4899)' : 'transparent',
                  color: selectedOption === SpecialEffect.PULSE ? '#ffffff' : '#e0e0e0'
                }}
                onClick={() => handleOptionChange(SpecialEffect.PULSE)}
                className={`${styles.button} `}
              >
                {t('captionEditor.editSegment.specialEffects.pulse')}
              </button>
            </div>
          </Group>
        </SimpleAccordion>
      </Stack>
    </div>
  );
};

export default memo(EditSegmentTooltip);





